import { useEffect, useRef, useState } from 'react';
import { IconArrowRight, IconConfetti, IconX } from '@tabler/icons-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import contestService from '../../services/contestService';
import styles from './ContestBanner.module.scss';

const CONTEST_URL = 'https://contest.sohozkaj.com/';

// Once the user closes the banner we remember the moment and keep it hidden for
// 24 hours, after which the sliding banner appears again.
const STORAGE_KEY = 'contestBannerClosedAt';
const HIDE_DURATION_MS = 24 * 60 * 60 * 1000;

// Marquee scroll speed in pixels per second — the animation duration is derived
// from this so the text always moves at the same pace regardless of viewport.
const SCROLL_SPEED_PX_PER_SEC = 70;

/**
 * Slim, full-width sliding marquee announcing the contest. Renders nothing unless Laravel has a
 * contest switched on:
 *   live / upcoming → the contest's own notice (respects its showNotice toggle); clicking opens
 *                     the contest site in a new tab
 *   hold            → "results will be published very soon" (1 h after the end, not clickable)
 * A close button hides it for 24h.
 */
function ContestBanner() {
  const { t } = useTranslation();
  const [contest, setContest] = useState(null);
  const [phase, setPhase] = useState(null); // live | hold | upcoming — null = no contest on
  const [closed, setClosed] = useState(false);

  // Enough identical copies so a single half of the track always overflows the
  // viewport — measured below so the loop never leaves a blank gap on the right.
  const [copies, setCopies] = useState(6);
  const [duration, setDuration] = useState(48);

  const bannerRef = useRef(null);
  const trackRef = useRef(null);

  // Respect a previous close (within the last 24h) before doing anything else.
  useEffect(() => {
    try {
      const ts = Number(localStorage.getItem(STORAGE_KEY));
      if (ts && Date.now() - ts < HIDE_DURATION_MS) setClosed(true);
    } catch { /* localStorage unavailable — just show the banner */ }
  }, []);

  useEffect(() => {
    let mounted = true;
    contestService
      .getActiveContest()
      .then((res) => {
        const ph = res?.phase || (res?.active ? 'live' : 'inactive');
        if (mounted && (ph === 'live' || ph === 'pending' || ph === 'upcoming')) {
          setContest(res.contest || {});
          setPhase(ph);
        }
      })
      .catch(() => {
        // Silent — a contest API hiccup should never block the header.
      });
    return () => { mounted = false; };
  }, []);

  // live / upcoming → the contest's own notice · hold (1 h after the end) → "results soon".
  const isHold = phase === 'pending';
  const notice = isHold ? t('contest.leaderboard.pendingMessage') : (contest?.notice?.trim() || '');

  // Measure one copy + the banner width and render just enough copies so half
  // the track (one full set) is always wider than the viewport. This keeps the
  // -50% loop seamless and gap-free on any screen size.
  useEffect(() => {
    if (!notice || closed) return;
    const measure = () => {
      const first = trackRef.current?.firstElementChild;
      const banner = bannerRef.current;
      if (!first || !banner) return;
      const copyWidth = first.getBoundingClientRect().width;
      const bannerWidth = banner.getBoundingClientRect().width;
      if (!copyWidth || !bannerWidth) return;
      const needed = Math.ceil(bannerWidth / copyWidth) + 1;
      setCopies((prev) => (prev !== needed ? needed : prev));
      setDuration(Math.max(20, Math.round((needed * copyWidth) / SCROLL_SPEED_PX_PER_SEC)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (bannerRef.current) ro.observe(bannerRef.current);
    return () => ro.disconnect();
  }, [notice, closed]);

  // Nothing to announce → render nothing (no contest on, or the contest has no notice text).
  // The showNotice toggle controls the whole topbar banner (the hold message always shows).
  // Also hidden while inside the 24h close window.
  if (!contest || closed || !notice) return null;
  if (contest.showNotice === false && !isHold) return null;

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
    setClosed(true);
  };

  // The text content repeated inside each marquee group.
  const Content = () => (
    <span className={styles.group}>
      <IconConfetti size={16} className="mx-2 shrink-0" />
      <span className="font-semibold mr-2">{notice}</span>
      {isHold ? <span className="mr-10" /> : (
        <span className="inline-flex items-center gap-1 font-bold bg-white/20 rounded-full px-2.5 py-0.5 mr-10">
          {t('contest.cta')}
          <IconArrowRight size={14} />
        </span>
      )}
    </span>
  );

  // Two identical halves (copies × 2) keep the translateX(-50%) loop seamless;
  // sizing `copies` to the viewport guarantees each half overflows it.
  const items = Array.from({ length: copies * 2 });

  const trackEl = (
    <span ref={trackRef} className={styles.marquee} style={{ animationDuration: `${duration}s` }}>
      {items.map((_, i) => <Content key={i} />)}
    </span>
  );


  return (
    <div
      ref={bannerRef}
      className={`${styles.banner} relative text-white text-xs sm:text-sm bg-[linear-gradient(90deg,#FBA53A_0%,#F4791A_52%,#EB5E10_100%)]`}
      style={{
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
      }}
    >
      {isHold ? (
        <div className="block overflow-hidden whitespace-nowrap py-1.5 pr-8 cursor-default">{trackEl}</div>
      ) : (
        <a
          href={CONTEST_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('contest.ariaLabel')}
          className="group/contest block overflow-hidden whitespace-nowrap py-1.5 pr-8"
        >
          {trackEl}
        </a>
      )}

      <button
        type="button"
        onClick={handleClose}
        aria-label={t('contest.close')}
        title={t('contest.close')}
        className="absolute top-1/2 right-1.5 -translate-y-1/2 z-10 flex items-center justify-center w-6 h-6 rounded-full bg-white hover:bg-zinc-100 text-zinc-700 shadow-sm ring-1 ring-black/10 transition-colors"
      >
        <IconX size={15} />
      </button>
    </div>
  );
}

export default ContestBanner;
